/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSlice } from '@reduxjs/toolkit';
import { signInActions } from 'modules/sign-in/slice';
import { ALIGNED, BOOKING_DATE, SLICE, TAGS } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import { getHotelFocusOnValue } from 'modules/dashboard/functions';
import { isEmpty } from 'modules/common/helpers/object';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';

/**
 * Initial states of Search widget functions are defined here
 */
//

const stayDate = {
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  key: 'selection',
};
const trendStayDate = {
  startDate: new Date(startOfMonth(subMonths(new Date(), 1))).toISOString(),
  endDate: new Date(endOfMonth(subMonths(new Date(), 1))).toISOString(),
  key: 'selection',
};
//
const bookingStartDate = new Date(BOOKING_DATE).toISOString();
//
export const initialState = {
  loading: false,
  favoriteScenarioLoading: false,
  searchData: [],
  scenarios: [],
  favoriteScenarios: [],
  searchBookingDate: null,
  chartBreakdown: null,
  dateComparison: null,
  searchFilterList: [],
  searchAlignmentToggle: null,
  searchOtb: false,
  searchCustomDate: 'start',
  searchStartDate: stayDate.startDate,
  searchEndDate: stayDate.endDate,
  searchDateSelected: stayDate.startDate,
  searchVisualToggle: false,
  searchUrl: '',
  symmetricComparisonToggle: true,
  isDetailed: false,
  paceVisualFilterList: [],
  alignment: null,
  symmetricComparisonIsDisplay: false,
  paceVisualIsDisplay: true,
  revenueSetIsDisplay: true,
  cxlSetIsDisplay: false,
  enableSecondaryHotelGroups: false,
  hotelFocusOn: {},
  normalizedView: false,
  searchHotelVisuals: [],
  searchVisuals: [],
  isSearchHotelComparison: false,
  percentage: null,
  //
  focusOn: [],
  searchTimeStartDate: trendStayDate.startDate,
  searchTimeEndDate: trendStayDate.endDate,
  trendTimeWeeklyView: false,
  isTrendDeAggregate: false,
  isTrendForecast: false,
  isTrendPercentageChange: false,
  isMovingAverage: false,
  isCumulative: false,
  granularity: null,
  movingAverage: null,
  trendTimeFilterList: [],
  trendTimeTabularFilterList: [],
  trendHotelFilterList: [],
  trendHotelTabularFilterList: [],
  adjustedStartDate: new Date(bookingStartDate).toISOString(),
  adjustedEndDate: new Date().toISOString(),
  segmentGroup1: [],
  segmentGroup2: [],
  trendSegmentFilterList: [],
  trendSegmentTabularFilterList: [],
  //
  originalFocusOn: null,
  isSearchLeapComparison: false,
  selectedScenario: null,
};
/**
 * All actions related to Search widget feature are defined here
 */
export const searchSlice = createSlice({
  name: SLICE.SEARCH,
  initialState,
  reducers: {
    getSearchScenarioData(state) {
      state.loading = true;
    },
    getSearchScenarioDataSucceeded(state, action) {
      state.loading = false;
      state.searchData = action.payload;
    },
    getSearchScenarioDataFailed(state) {
      state.loading = false;
    },
    setSearchScenario(state, action) {
      state.searchData = action.payload;
    },
    //
    getScenarios(state) {
      state.loading = true;
    },
    getScenariosSucceeded(state, action) {
      state.loading = false;
      state.scenarios = action.payload;
    },
    getScenariosFailed(state) {
      state.loading = false;
    },
    setScenarios(state, action) {
      state.scenarios = action.payload;
    },
    //
    getFavoriteScenario(state) {
      state.favoriteScenarioLoading = true;
    },
    getFavoriteScenarioSucceeded(state, action) {
      state.favoriteScenarioLoading = false;
      state.favoriteScenarios = action.payload;
    },
    getFavoriteScenarioFailed(state) {
      state.favoriteScenarioLoading = false;
    },
    setFavoriteScenario(state, action) {
      state.favoriteScenarios = action.payload;
    },
    //
    updateFavoriteScenario(state) {
      state.favoriteScenarioLoading = true;
    },
    updateFavoriteScenarioSucceeded(state) {
      state.favoriteScenarioLoading = false;
    },
    updateFavoriteScenarioFailed(state) {
      state.favoriteScenarioLoading = false;
    },
    //
    setSearchBookingDate(state, action) {
      state.searchBookingDate = action?.payload;
    },
    setBreakdown(state, action) {
      state.chartBreakdown = action?.payload;
    },
    setDateComparison(state, action) {
      state.dateComparison = action?.payload;
    },
    updateSearchFilterList(state, action) {
      state.searchFilterList = action?.payload;
    },
    setSearchAlignmentToggle(state, action) {
      state.searchAlignmentToggle = action?.payload;
    },
    setSearchOtb(state, action) {
      state.searchOtb = action?.payload;
    },
    setSearchCustomDate(state, action) {
      state.searchCustomDate = action?.payload;
    },
    setSearchStartDate(state, action) {
      state.searchStartDate = action?.payload;
    },
    setSearchEndDate(state, action) {
      state.searchEndDate = action?.payload;
    },
    setSearchDateSelected(state, action) {
      state.searchDateSelected = action?.payload;
    },
    setSearchVisualToggle(state, action) {
      state.searchVisualToggle = action.payload;
    },
    setSearchUrl(state, action) {
      state.searchUrl = action.payload;
    },
    setSymmetricComparisonToggle(state, action) {
      state.symmetricComparisonToggle = action?.payload;
    },
    setIsDetailed(state, action) {
      state.isDetailed = action?.payload;
    },
    setPaceVisualFilterList(state, action) {
      state.paceVisualFilterList = action?.payload;
    },
    setAlignment(state, action) {
      state.alignment = action?.payload;
    },
    setSymmetricComparisonIsDisplay(state, action) {
      state.symmetricComparisonIsDisplay = action?.payload;
    },
    setPaceVisualIsDisplay(state, action) {
      state.paceVisualIsDisplay = action?.payload;
    },
    setRevenueSetIsDisplay(state, action) {
      state.revenueSetIsDisplay = action?.payload;
    },
    setCxlSetIsDisplay(state, action) {
      state.cxlSetIsDisplay = action?.payload;
    },
    setEnableSecondaryHotelGroups(state, action) {
      state.enableSecondaryHotelGroups = action.payload;
    },
    setHotelFocusOn(state, action) {
      state.hotelFocusOn = action?.payload;
      state.enableSecondaryHotelGroups = action?.payload && 'hotels' in action.payload;
    },
    setNormalizedView(state, action) {
      state.normalizedView = action?.payload;
    },
    setIsSearchHotelComparison(state, action) {
      state.isSearchHotelComparison = action?.payload;
    },
    setOriginalHotelFocusOn: (state, action) => {
      state.originalFocusOn = action.payload;
    },
    resetHotelFocusOn: (state, action) => {
      if (state.originalFocusOn !== null) {
        state.hotelFocusOn = action?.payload;
        state.enableSecondaryHotelGroups = action?.payload && 'hotels' in action.payload;
      }
    },
    setIsSearchLeapComparison(state, action) {
      state.isSearchLeapComparison = action?.payload;
    },
    setPercentage(state, action) {
      state.percentage = action?.payload;
    },
    setFocusOn(state, action) {
      state.focusOn = action?.payload;
    },
    setTrendTimeWeeklyView(state, action) {
      state.trendTimeWeeklyView = action?.payload;
    },
    setSearchTimeStartDate(state, action) {
      state.searchTimeStartDate = action?.payload;
    },
    setSearchTimeEndDate(state, action) {
      state.searchTimeEndDate = action?.payload;
    },
    setIsCumulative(state, action) {
      state.isCumulative = action?.payload;
    },
    setIsTrendForecast(state, action) {
      state.isTrendForecast = action?.payload;
    },
    setIsTrendPercentageChange(state, action) {
      state.isTrendPercentageChange = action?.payload;
    },
    setGranularity(state, action) {
      state.granularity = action?.payload;
    },
    setMovingAverage(state, action) {
      state.movingAverage = action?.payload;
    },
    updateTrendTimeFilterList(state, action) {
      state.trendTimeFilterList = action?.payload;
    },
    updateTrendTimeTabularFilterList(state, action) {
      state.trendTimeTabularFilterList = action?.payload;
    },
    updateTrendHotelFilterList(state, action) {
      state.trendHotelFilterList = action?.payload;
    },
    updateTrendHotelTabularFilterList(state, action) {
      state.trendHotelTabularFilterList = action?.payload;
    },
    setAdjustedStartDate(state, action) {
      state.adjustedStartDate = action?.payload;
    },
    setAdjustedEndDate(state, action) {
      state.adjustedEndDate = action?.payload;
    },
    setSegmentGroup1(state, action) {
      state.segmentGroup1 = action?.payload;
    },
    setSegmentGroup2(state, action) {
      state.segmentGroup2 = action?.payload;
    },
    updateTrendSegmentFilterList(state, action) {
      state.trendSegmentFilterList = action?.payload;
    },
    updateTrendSegmentTabularFilterList(state, action) {
      state.trendSegmentTabularFilterList = action?.payload;
    },
    setIsMovingAverage(state, action) {
      state.isMovingAverage = action?.payload;
    },
    setIsTrendDeAggregate(state, action) {
      state.isTrendDeAggregate = action?.payload;
    },
    setSelectedScenario(state, action) {
      state.selectedScenario = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(dashboardActions.getVisualFiltersSucceeded, (state, action) => {
        const dateComparisonFilter = action?.payload?.results?.find(
          (filter) => filter?.tags?.includes(TAGS.DATE_COMPARISON) && filter?.isDefault === true
        );
        const movingAverage = action?.payload?.results?.find(
          (filter) => filter?.tags?.includes(TAGS.MOVING_AVERAGE) && filter?.isDefault === true
        );
        return {
          ...state,
          chartBreakdown: action?.payload?.results?.find(
            (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
          )?.id,
          dateComparison: dateComparisonFilter?.values?.find((filter) => filter.isDefault === true)
            ?.value,
          alignment: {
            id: dateComparisonFilter?.id,
            label: capitalizeFirst(dateComparisonFilter?.type),
          },
          percentage: action?.payload?.results?.find(
            (filter) => filter?.tags?.includes(TAGS.PERCENTAGE) && filter?.isDefault === true
          )?.id,
          searchAlignmentToggle: dateComparisonFilter?.type === ALIGNED,
          movingAverage: { id: movingAverage?.id, label: movingAverage?.values[0] },
        };
      })
      .addCase(dashboardActions.getHotelListSucceeded, (state, action) => {
        const hotel = getHotelFocusOnValue(
          state.isDemoUser,
          action?.payload,
          state.isDemoModeActivated
        );
        return {
          ...state,
          hotelFocusOn: hotel?.focusOnValues?.[0],
        };
      })
      .addCase(dashboardActions.getVisualsSucceeded, (state, action) => {
        const visualDetails = action?.payload?.results;
        return {
          ...state,
          searchVisuals: visualDetails,
        };
      })
      .addCase(dashboardActions.setHotelName, (state, action) => ({
        ...state,
        hotelFocusOn: action?.payload,
        originalFocusOn: action?.payload,
        normalizedView: false,
        enableSecondaryHotelGroups: action?.payload && 'hotels' in action.payload,
      }))

      .addCase(dashboardActions.setSelectedHotelGroup, (state, action) => {
        let secondaryHotelSet;
        if (state && state.hotelFocusOn && 'hotels' in state.hotelFocusOn) {
          if (!isEmpty(state.hotelName) && action?.payload?.hotelGroupDropdownList?.length === 0) {
            secondaryHotelSet = action?.payload?.hotelList[0];
          } else if (state.hotelFocusOn && action?.payload?.hotelGroupDropdownList?.length > 0) {
            const newGroup = action?.payload?.hotelGroupDropdownList?.find(
              (set) => set?.id === state.hotelFocusOn?.id
            );

            if (newGroup) {
              secondaryHotelSet = newGroup;
            } else {
              secondaryHotelSet = action?.payload?.hotelList[0];
            }
          } else {
            secondaryHotelSet = action?.payload?.hotelList[0];
          }
          return {
            ...state,
            hotelFocusOn: secondaryHotelSet,
            enableSecondaryHotelGroups: secondaryHotelSet && 'hotels' in secondaryHotelSet,
          };
        }
        return { ...state };
      })
      .addCase(dashboardActions.setEnableHotelGroups, (state, action) => ({
        ...state,
        enableSecondaryHotelGroups: action?.payload,
        normalizedView: false,
      }));
  },
});
//
export const { actions: searchActions } = searchSlice;
